import React from 'react';
import { hasLowerCase, hasNumber, hasSpecialCharacter, hasUpperCase } from 'utils/form';
import s from './PasswordValidation.module.less';

interface PasswordValidationProps {
    /** 密码值 */
    password: string;
    /** 是否显示校验指示器 */
    visible?: boolean;
}

// 密码错误类型枚举，与现有SignUpPage保持一致
enum PasswordError {
    LOWERCASE = 'lowercase',
    SPECIAL = 'special',
    UPPERCASE = 'uppercase',
    MINIMUM = 'minimum',
    NUMBER = 'number',
}

interface ValidationRule {
    /** 规则标识 */
    key: PasswordError;
    /** 规则描述 */
    label: string;
    /** 校验函数 */
    validator: (password: string) => boolean;
}

// 密码校验规则，使用现有的工具函数
const validationRules: ValidationRule[] = [
    {
        key: PasswordError.LOWERCASE,
        label: 'One lowercase character',
        validator: hasLowerCase,
    },
    {
        key: PasswordError.UPPERCASE,
        label: 'One uppercase character',
        validator: hasUpperCase,
    },
    {
        key: PasswordError.NUMBER,
        label: 'One number',
        validator: hasNumber,
    },
    {
        key: PasswordError.SPECIAL,
        label: 'One special character',
        validator: hasSpecialCharacter,
    },
    {
        key: PasswordError.MINIMUM,
        label: '8 characters minimum',
        validator: (password: string) => password.length >= 8,
    },
];

/**
 * 密码校验指示器组件
 * 根据Figma设计实现，显示密码各项要求的满足状态
 */
export default function PasswordValidation({ password, visible = true }: PasswordValidationProps) {
    if (!visible) {
        return null;
    }

    // 将规则分为两列显示
    const leftColumnRules = validationRules.slice(0, 3);
    const rightColumnRules = validationRules.slice(3);

    const renderValidationRule = (rule: ValidationRule) => {
        const isValid = rule.validator(password);

        return (
            <div key={rule.key} className={s.validationItem}>
                <div className={`${s.dot} ${isValid ? s.dotValid : s.dotInvalid}`} />
                <span className={s.label}>{rule.label}</span>
            </div>
        );
    };

    return (
        <div className={s.validationContainer}>
            <div className={s.validationColumn}>
                {leftColumnRules.map(renderValidationRule)}
            </div>
            <div className={s.validationColumn}>
                {rightColumnRules.map(renderValidationRule)}
            </div>
        </div>
    );
}

/**
 * 获取密码错误列表，与现有SignUpPage实现保持一致
 */
export const getPasswordErrors = (password: string): PasswordError[] => {
    const errors: PasswordError[] = [];
    if (!hasLowerCase(password)) {
        errors.push(PasswordError.LOWERCASE);
    }
    if (!hasNumber(password)) {
        errors.push(PasswordError.NUMBER);
    }
    if (!hasSpecialCharacter(password)) {
        errors.push(PasswordError.SPECIAL);
    }
    if (!hasUpperCase(password)) {
        errors.push(PasswordError.UPPERCASE);
    }
    if (password.length < 8) {
        errors.push(PasswordError.MINIMUM);
    }
    return errors;
};

/**
 * 检查密码是否满足所有要求
 */
export const isPasswordValid = (password: string): boolean => {
    return getPasswordErrors(password).length === 0;
};

/**
 * 获取密码校验结果
 */
export const getPasswordValidation = (password: string) => {
    const errors = getPasswordErrors(password);
    return validationRules.map((rule) => ({
        key: rule.key,
        label: rule.label,
        isValid: !errors.includes(rule.key),
    }));
};

// 导出PasswordError枚举供外部使用
export { PasswordError };
